/* eslint-disable react-refresh/only-export-components */
import React, { useMemo } from 'react';
import { createCtx } from '../../../../../components/Contexts/createCtx';
import { useLocalStorage } from '../../../../../hooks/useLocalStorageNew';

const GRID_COLUMN_GAP = 10;

export type GridLayoutContext = {
    gap: number;
    columnWidths: number[];
    correctionColumnWidths: number[];
    totalWidth: number;
    correctionTotalWidth: number;
    gridColumnTemplate: string;
    correctionGridColumnTemplate: string;
    gridRowStyles: React.CSSProperties;
    correctionGridRowStyles: React.CSSProperties;
    rowHeaderWidth: number;
    setRowHeaderWidth: React.Dispatch<React.SetStateAction<number>>;
};

const [useCtx, Provider] = createCtx<GridLayoutContext>();

export const useGridLayoutContext = useCtx;

export const GridLayoutContextProvider = ({ children }: { children: React.ReactNode }) => {
    const [rowHeaderWidth, setRowHeaderWidth] = useLocalStorage('time-entry-corrections-row-header-width', 400);

    const columnWidths = useMemo(() => {
        return [
            40, // Expand/Collapse icon column
            rowHeaderWidth, // Project/User/Task hierarchy
            140, // Registered Hours
            120, // Date
            100, // Actions
        ];
    }, [rowHeaderWidth]);

    // Column widths for correction rows (6 columns + extra padding, removed Original column and Actions column, merged Description with Comment)
    const correctionColumnWidths = useMemo(() => {
        return [
            40, // Expand/Collapse column (to align with main grid)
            400, // Comment (increased width since it's now the main description)
            120, // Corrected
            120, // Difference
            150, // Date
            150, // Created By
            50, // Extra padding at the end
        ];
    }, []);

    const totalWidth = useMemo(() => columnWidths.reduce((acc, width) => acc + width + GRID_COLUMN_GAP, 0), [columnWidths]);

    const correctionTotalWidth = useMemo(() => correctionColumnWidths.reduce((acc, width) => acc + width + GRID_COLUMN_GAP, 0), [correctionColumnWidths]);

    const gridColumnTemplate = useMemo(() => columnWidths.reduce((acc, width) => acc + `${width}px `, ''), [columnWidths]);

    const correctionGridColumnTemplate = useMemo(() => correctionColumnWidths.reduce((acc, width) => acc + `${width}px `, ''), [correctionColumnWidths]);

    const gridRowStyles = useMemo(
        (): React.CSSProperties => ({
            display: 'grid',
            gridTemplateColumns: gridColumnTemplate,
            gap: GRID_COLUMN_GAP,
            alignItems: 'center',
        }),
        [gridColumnTemplate],
    );

    const correctionGridRowStyles = useMemo(
        (): React.CSSProperties => ({
            display: 'grid',
            gridTemplateColumns: correctionGridColumnTemplate,
            gap: GRID_COLUMN_GAP,
            alignItems: 'center',
        }),
        [correctionGridColumnTemplate],
    );

    const context = useMemo((): GridLayoutContext => {
        return {
            columnWidths,
            correctionColumnWidths,
            gap: GRID_COLUMN_GAP,
            gridColumnTemplate,
            correctionGridColumnTemplate,
            gridRowStyles,
            correctionGridRowStyles,
            totalWidth,
            correctionTotalWidth,
            rowHeaderWidth,
            setRowHeaderWidth,
        };
    }, [
        columnWidths,
        correctionColumnWidths,
        gridColumnTemplate,
        correctionGridColumnTemplate,
        gridRowStyles,
        correctionGridRowStyles,
        rowHeaderWidth,
        setRowHeaderWidth,
        totalWidth,
        correctionTotalWidth,
    ]);

    return <Provider value={context}>{children}</Provider>;
};
